
























































































































































































































































































































import {
  Alert,
  AlertAction,
  AlertActionOnType,
  AlertActionType,
  AlertCondition,
  AlertConditionAggregationType,
  AlertConditionMetric,
  AlertConditionOperator,
  ConnectedRealm,
  CreateOrPutAlertActionRequest,
  CreateOrPutAlertConditionRequest,
  CreateOrPutAlertForUserRequest,
  Realm,
  WoWItem
} from '@/models';
import { UserMixin } from '@/mixins/UserMixin';
import Vue, { PropType, VueConstructor } from 'vue';
import { cloneDeep, debounce } from 'lodash';
import { ArrayUtilities, ColorUtilities, Utilities } from '@/utilities';
import { realmService, uiSettingsService, wowItemService, loadingService } from '@/services';
import { WoWItemQualityColor } from '@/models/blizzard';
import { alertService } from '@/services/AlertService';
import { from } from 'typescript-extended-linq';

export default (Vue as VueConstructor<Vue & InstanceType<typeof UserMixin>>).extend({
  name: 'AlertDesigner',

  mixins: [UserMixin],

  props: {
    value: Boolean,
    wowItemId: {
      type: Number,
      required: false
    },
    initialAlertPrice: {
      type: Number,
      required: false,
      default: 10000
    },
    connectedRealmId: {
      type: Number,
      required: false
    },
    alert: {
      type: Object as PropType<Alert>,
      required: false
    }
  },

  data: () => ({
    errors: [] as string[],
    formValid: false,
    loading: false,
    nameRules: [(name: string) => !!name || 'Name is required'],
    itemsLookup: new Map<number, WoWItem>(),
    itemsLoading: false,
    items: [] as WoWItem[],
    itemsSearchTerm: null as string | null,
    realms: [] as Realm[],
    realmsLookup: new Map<number, Realm>(),
    connectedRealms: new Map<number, ConnectedRealm>(),
    addOrUpdateConditionDialog: {
      targetRules: [
        (target: string) => !!target || 'Target is required',
        (target: string) => Utilities.validateEmail(target) || 'Please enter a valid email'
      ],
      thresholdRules: [(threshold: number) => threshold > 0 || 'Threshold must be greater than 0'],
      showDialog: false,
      formValid: false,
      metrics: Object.values(AlertConditionMetric).map(value => ({
        text: Utilities.splitAtUpperCase(value),
        value
      })),
      operators: Object.values(AlertConditionOperator).map(value => ({
        text: Utilities.splitAtUpperCase(value),
        value
      })),
      aggregations: Object.values(AlertConditionAggregationType).map(value => ({
        text: Utilities.splitAtUpperCase(value),
        value
      })),
      currentIndex: null as number | null,
      newOrUpdatedCondition: {} as CreateOrPutAlertConditionRequest & { wowItem?: WoWItem | null; realm?: Realm | null }
    },
    addOrUpdateActionDialog: {
      targetRules: [
        (target: string) => !!target || 'Target is required',
        (target: string) => Utilities.validateEmail(target) || 'Please enter a valid email'
      ],
      showDialog: false,
      formValid: false,
      actionOnTypes: Object.values(AlertActionOnType).map(value => ({
        text: Utilities.splitAtUpperCase(value),
        value
      })),
      actionTypes: Object.values(AlertActionType).map(value => ({
        text: Utilities.splitAtUpperCase(value),
        value
      })),
      currentIndex: null as number | null,
      newOrUpdatedAction: {} as CreateOrPutAlertActionRequest
    },
    alertToModify: {
      name: '',
      description: '',
      conditions: [],
      actions: []
    } as CreateOrPutAlertForUserRequest
  }),

  methods: {
    getActionText(action: AlertAction | CreateOrPutAlertActionRequest): string {
      return `When alert ${
        action.actionOn === AlertActionOnType.AlertActivated ? 'activates' : 'deactivates'
      }, send ${action.type.toLowerCase()} to ${action.target}.`;
    },

    removeCondition(condition: CreateOrPutAlertConditionRequest): void {
      ArrayUtilities.removeItem(this.alertToModify.conditions, condition);
    },

    removeAction(action: CreateOrPutAlertActionRequest): void {
      ArrayUtilities.removeItem(this.alertToModify.actions, action);
    },

    addOrUpdateAction(): void {
      if (!this.addOrUpdateActionDialog.formValid || !this.addOrUpdateActionDialog.newOrUpdatedAction) {
        console.error('Add or update action form invalid.');
        return;
      }

      if (typeof this.addOrUpdateActionDialog.currentIndex === 'number') {
        this.alertToModify.actions[this.addOrUpdateActionDialog.currentIndex] = {
          ...this.addOrUpdateActionDialog.newOrUpdatedAction
        };
      } else {
        this.alertToModify.actions.push({ ...this.addOrUpdateActionDialog.newOrUpdatedAction });
      }

      this.addOrUpdateActionDialog.showDialog = false;
      this.resetAddOrUpdateActionForm();
    },

    getItemQualityColor(quality: string, id: number): WoWItemQualityColor {
      return ColorUtilities.getItemQualityColor(quality, uiSettingsService.darkThemeSet, id);
    },

    genFilteredColoredText(
      text: string,
      textColor: string,
      parent?: { genFilteredText(text: string): string }
    ): string {
      let result = parent?.genFilteredText(text) ?? '';
      const search = '<span';
      const spanIndex = result.indexOf(search);

      if (spanIndex >= 0) {
        result = Utilities.insert(result, spanIndex + search.length, ` style="color: ${textColor}"`);
      }

      return `<span style="color: ${textColor}">${result}</span>`;
    },

    addOrUpdateCondition(): void {
      if (!this.addOrUpdateConditionDialog.formValid || !this.addOrUpdateConditionDialog.newOrUpdatedCondition) {
        console.error('Add or update condition form invalid.');
        return;
      }

      if (typeof this.addOrUpdateConditionDialog.currentIndex === 'number') {
        this.alertToModify.conditions[this.addOrUpdateConditionDialog.currentIndex] = {
          ...this.addOrUpdateConditionDialog.newOrUpdatedCondition
        };
      } else {
        this.alertToModify.conditions.push({ ...this.addOrUpdateConditionDialog.newOrUpdatedCondition });
      }

      this.addOrUpdateConditionDialog.showDialog = false;
      this.resetAddOrUpdateConditionForm();
    },

    openAddOrUpdateActionDialog(index?: number): void {
      if (typeof index === 'number') {
        this.addOrUpdateActionDialog.currentIndex = index;
        this.addOrUpdateActionDialog.newOrUpdatedAction = { ...this.alertToModify.actions[index] };
      } else {
        this.addOrUpdateActionDialog.currentIndex = null;
        this.addOrUpdateActionDialog.newOrUpdatedAction = {
          type: AlertActionType.Email,
          actionOn: AlertActionOnType.AlertActivated,
          target: this.user.email
        };
      }

      this.addOrUpdateActionDialog.showDialog = true;
    },

    openAddOrUpdateConditionDialog(index?: number): void {
      if (typeof index === 'number') {
        this.addOrUpdateConditionDialog.currentIndex = index;
        this.addOrUpdateConditionDialog.newOrUpdatedCondition = {
          ...this.alertToModify.conditions[index],
          wowItem: this.items.find(item => item.id === this.alertToModify.conditions[index].wowItemId), // can't use lookup due to needing item from autocomplete box
          realm: this.realms.find(
            realm => realm.connectedRealmId === this.alertToModify.conditions[index].connectedRealmId
          ) // can't use lookup due to needing item from autocomplete box
        };
      } else {
        this.addOrUpdateConditionDialog.currentIndex = null;
        this.addOrUpdateConditionDialog.newOrUpdatedCondition = {
          wowItem: this.wowItemId ? this.items.find(item => item.id === this.wowItemId) : null, // can't use lookup due to needing item from autocomplete box
          wowItemId: this.wowItemId ?? 0,
          connectedRealmId: this.connectedRealmId ?? 0,
          realm: this.connectedRealmId
            ? this.realms.find(realm => realm.connectedRealmId === this.connectedRealmId)
            : null, // can't use lookup due to needing item from autocomplete box
          metric: AlertConditionMetric.MinPrice,
          operator: AlertConditionOperator.LessThan,
          threshold: this.initialAlertPrice,
          aggregationType: AlertConditionAggregationType.Max,
          aggregationTimeGranularityInHours: 1
        };
      }

      this.addOrUpdateConditionDialog.showDialog = true;
    },

    getConditionString(condition: AlertCondition | CreateOrPutAlertConditionRequest): string {
      const item = this.itemsLookup.get(condition.wowItemId);
      const realms = this.connectedRealms.get(condition.connectedRealmId)?.realms ?? [];
      const { g, s, c } = Utilities.convertToGoldSilverCopper(condition.threshold);

      return `When ${Utilities.splitAtUpperCase(
        condition.aggregationType
      ).toLowerCase()} of ${Utilities.splitAtUpperCase(
        condition.metric
      ).toLowerCase()} for <a href="https://www.wowhead.com/item=${item?.id}" target="_blank">${
        item?.name
      }</a> for ${realms.reduce(
        (prevStr, currRealm, j) =>
          `${prevStr}${currRealm.name}${
            j !== realms.length - 1
              ? `${j === realms.length - 2 ? `${realms.length > 2 ? ', and ' : ' and '}` : ', '}`
              : ''
          }`,
        ''
      )} is ${Utilities.splitAtUpperCase(condition.operator).toLowerCase()} ${
        condition.metric === AlertConditionMetric.TotalAvailableForAuction
          ? condition.threshold.toLocaleString('en-US')
          : `${g.toLocaleString('en-US')}g ${s}s ${c}c`
      } over period of ${condition.aggregationTimeGranularityInHours} hour${
        condition.aggregationTimeGranularityInHours === 1 ? '' : 's'
      }.`;
    },

    validateAlert(): { valid: boolean; errors: string[] } {
      let errors: string[] = [];

      if (!this.alertToModify.name) {
        errors.push('Name is required.');
      }

      if (this.alertToModify.actions.length === 0) {
        errors.push('At least one action is required.');
      }

      if (this.alertToModify.conditions.length === 0) {
        errors.push('At least one condition is required.');
      }

      return { valid: errors.length === 0, errors };
    },

    async saveAlert(): Promise<void> {
      const { valid, errors } = this.validateAlert();

      if (!valid) {
        this.errors = [...errors];
        return;
      }

      try {
        loadingService.startLoading();

        if (this.alert) {
          await this.updateAlert();
        } else {
          await this.createAlert();
        }
      } finally {
        loadingService.stopLoading();
      }

      this.closeDialog();
    },

    async createAlert(): Promise<void> {
      try {
        const newAlert = await alertService.createAlertForUser(this.userId, this.alertToModify);
        this.onAlertCreated(newAlert);
      } catch (error) {
        console.log(error);
      }
    },

    async updateAlert(): Promise<void> {
      try {
        if (!this.alert) {
          throw new Error('No alert to modify');
        }
        const updatedAlert = await alertService.putAlertForUser(this.userId, this.alert.id, this.alertToModify);
        this.onAlertUpdated(updatedAlert);
      } catch (error) {
        console.log(error);
      }
    },

    onAlertUpdated(updatedAlert: Alert): void {
      this.$emit('alert-updated', updatedAlert);
    },

    onAlertCreated(newAlert: Alert): void {
      this.$emit('alert-created', newAlert);
    },

    closeDialog(): void {
      this.resetUpsertAlertForm();
      this.errors = [];
      this.show = false;
    },

    resetUpsertAlertForm(): void {
      (this.$refs.upsertAlertForm as any).reset();
    },

    resetAddOrUpdateActionForm(): void {
      this.addOrUpdateActionDialog.currentIndex = null;
      (this.$refs.addOrUpdateActionForm as any).reset();
    },

    resetAddOrUpdateConditionForm(): void {
      this.addOrUpdateConditionDialog.currentIndex = null;
      (this.$refs.addOrUpdateConditionForm as any).reset();
    },

    async loadItems(): Promise<void> {
      if (this.alert) {
        const items = (
          await Promise.all(
            [
              ...new Set(
                typeof this.wowItemId === 'number'
                  ? [...this.alert.conditions.map(condition => condition.wowItemId), this.wowItemId]
                  : this.alert.conditions.map(condition => condition.wowItemId)
              )
            ].map(itemId => wowItemService.getItem(itemId))
          )
        ).filter(item => item !== null) as WoWItem[];

        this.items = from([...this.items, ...items])
          .distinctBy(item => item.id)
          .pipe(item => this.itemsLookup.set(item.id, item))
          .toArray();
      } else if (typeof this.wowItemId === 'number') {
        const item = await wowItemService.getItem(this.wowItemId);

        if (item) {
          this.items = [item];
          this.itemsLookup.set(this.wowItemId, item);
        }
      }
    },

    async searchItems(searchTerm: string): Promise<void> {
      this.itemsLoading = true;

      try {
        this.items = (await wowItemService.getItems({ nameLike: searchTerm, first: 25 }))
          .concat(this.items)
          .distinctBy(item => item.id)
          .pipe(item => this.itemsLookup.set(item.id, item))
          .orderBy(item => item.name)
          .toArray();
      } catch (error) {
        console.error(error);
      } finally {
        this.itemsLoading = false;
      }
    },

    // Must use 'function' syntax for correct 'this' binding
    debouncedSearchItems: debounce(function (
      this: Vue & { searchItems(searchTerm: string): Promise<void> },
      newValue: string
    ): void {
      this.searchItems(newValue);
    },
    500)
  },

  async mounted(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      this.realms = (await realmService.getConnectedRealms())
        .pipe(connectedRealm => this.connectedRealms.set(connectedRealm.id, connectedRealm))
        .selectMany(connectedRealm => connectedRealm.realms)
        .pipe(realm => this.realmsLookup.set(realm.id, realm))
        .orderBy(realm => realm.name)
        .toArray();

      await this.loadItems();
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  },

  watch: {
    async wowItemId(): Promise<void> {
      const item = await wowItemService.getItem(this.wowItemId);

      if (item) {
        this.items.push(item);
        this.itemsLookup.set(item.id, item);
      }
    },

    async alert(): Promise<void> {
      this.loading = true;

      try {
        this.alertToModify = cloneDeep(this.alert) ?? {
          name: '',
          description: '',
          conditions: [],
          actions: []
        };

        await this.loadItems();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    itemsSearchTerm(newValue: string | null): void {
      if (!newValue) {
        return;
      }

      this.debouncedSearchItems(newValue);
    }
  },

  computed: {
    show: {
      get(): boolean {
        return this.value;
      },

      set(value: boolean): void {
        this.$emit('input', value);
      }
    }
  }
});
